import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { getTickets, getResources, getWidgetSummary, getAdminConfig, getFCRList, getFCRCount, acknowledgeTicketReply, updateTicket, mergeTicket, toggleProjectFlag, getIntakeStatusSummary } from '../services/api';
import { realtime, type TicketChangedPayload } from '../services/realtimeClient';
import { Ticket, Resource, WidgetStat, ForwardedRequest } from '../services/types';
import PageHeader from '../components/PageHeader';
import TicketDetailModal from '../components/TicketDetailModal';
import { StaleAuditPanel } from '../components/StaleAuditPanel';
import NewTicketModal from '../components/NewTicketModal';
import FCRList from '../components/FCRList';
import FCRProcessModal from '../components/FCRProcessModal';
import { useAuth } from '../contexts/AuthProvider';
import FieldMapWidget from '../components/FieldMapWidget';

// Format time ago for last updated display
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

// Format ticket age as "Xd Xh" or "Xh Xm"
function formatAge(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays}d ${remainingHours}h`;
  }
  if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m`;
  }
  return `${diffMins}m`;
}

// Get date from ticket - handles both createDate and createdDate field names
function getTicketDate(ticket: Ticket): string | undefined {
  return (ticket as any).createdDate || ticket.createDate;
}

type FilterType = 'all' | 'new' | 'in-progress' | 'waiting-customer' | 'waiting-us' | 'project' | 'fcr';

interface ColumnFilters {
  ticketNumber: string;
  title: string;
  client: string;
  contact: string;
  assigned: string;
  priority: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

// Filter icon component
function FilterIcon({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`ml-1 p-0.5 rounded hover:bg-slate-600 ${active ? 'text-blue-400' : 'text-slate-500'}`}
      title="Filter"
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
    </button>
  );
}

// Text search filter popup with autocomplete suggestions
function TextFilterPopup({
  value,
  onChange,
  onClose,
  placeholder,
  suggestions
}: {
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
  placeholder: string;
  suggestions?: string[];
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Filter and sort suggestions based on current value
  const filteredSuggestions = suggestions
    ? suggestions
        .filter(s => s.toLowerCase().includes(value.toLowerCase()))
        .sort((a, b) => a.localeCompare(b))
    : [];

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [value]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('button');
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
          onChange(filteredSuggestions[selectedIndex]);
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div className="bg-slate-700 border border-slate-600 rounded-lg shadow-lg p-2 w-[280px]">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={`Search ${placeholder}...`}
        className="w-full bg-slate-600 text-white text-xs rounded px-2 py-1 border border-slate-500 focus:border-blue-500 focus:outline-none"
      />
      {/* Show sorted list - always visible when suggestions exist */}
      {filteredSuggestions.length > 0 && (
        <div
          ref={listRef}
          className="mt-1 max-h-[250px] overflow-y-auto border-t border-slate-600 pt-1"
        >
          {filteredSuggestions.map((s, index) => (
            <button
              key={s}
              onClick={() => { onChange(s); onClose(); }}
              className={`w-full text-left text-xs px-2 py-1 rounded truncate ${
                index === selectedIndex
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-600'
              }`}
              title={s}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {filteredSuggestions.length === 0 && suggestions && suggestions.length > 0 && (
        <div className="mt-1 text-xs text-slate-400 py-2 text-center">
          No matches found
        </div>
      )}
      <div className="flex justify-between items-center mt-2 gap-1 border-t border-slate-600 pt-2">
        <span className="text-xs text-slate-500">
          {filteredSuggestions.length} result{filteredSuggestions.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-1">
          <button onClick={() => { onChange(''); }} className="text-xs text-slate-400 hover:text-white px-2 py-1">Clear</button>
          <button onClick={onClose} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded">Done</button>
        </div>
      </div>
    </div>
  );
}

// Dropdown filter popup
function DropdownFilterPopup({
  value,
  options,
  onChange,
  onClose
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="bg-slate-700 border border-slate-600 rounded-lg shadow-lg p-2 min-w-[150px] max-h-[200px] overflow-y-auto">
      <button
        onClick={() => { onChange(''); onClose(); }}
        className={`w-full text-left text-xs px-2 py-1 rounded ${!value ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-600'}`}
      >
        All
      </button>
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => { onChange(opt); onClose(); }}
          className={`w-full text-left text-xs px-2 py-1 rounded ${value === opt ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-600'}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// Date range filter popup
function DateFilterPopup({
  dateFrom,
  dateTo,
  onChangeFrom,
  onChangeTo,
  onClose
}: {
  dateFrom: string;
  dateTo: string;
  onChangeFrom: (v: string) => void;
  onChangeTo: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="bg-slate-700 border border-slate-600 rounded-lg shadow-lg p-2 min-w-[220px]">
      <div className="text-xs text-slate-400 mb-1">From:</div>
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => onChangeFrom(e.target.value)}
        className="w-full bg-slate-600 text-white text-xs rounded px-2 py-1 border border-slate-500 focus:border-blue-500 focus:outline-none mb-2"
      />
      <div className="text-xs text-slate-400 mb-1">To:</div>
      <input
        type="date"
        value={dateTo}
        onChange={(e) => onChangeTo(e.target.value)}
        className="w-full bg-slate-600 text-white text-xs rounded px-2 py-1 border border-slate-500 focus:border-blue-500 focus:outline-none"
      />
      <div className="flex justify-end mt-2 gap-1">
        <button onClick={() => { onChangeFrom(''); onChangeTo(''); onClose(); }} className="text-xs text-slate-400 hover:text-white px-2 py-1">Clear</button>
        <button onClick={onClose} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded">Done</button>
      </div>
    </div>
  );
}

// Sort types for multi-column sorting
type SortDirection = 'asc' | 'desc';
type SortColumn = 'ticketNumber' | 'title' | 'client' | 'contact' | 'assigned' | 'created' | 'priority' | 'status' | 'age';

interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

// Sort indicator icon - always visible, shows order number for multi-sort
function SortIcon({
  direction,
  order,
  isActive
}: {
  direction?: SortDirection;
  order?: number;
  isActive: boolean;
}) {
  return (
    <span className={`ml-1 inline-flex items-center ${isActive ? 'text-blue-400' : 'text-slate-600'}`}>
      {direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '○'}
      {order !== undefined && order > 0 && isActive && (
        <span className="text-[9px] ml-0.5 bg-blue-500 text-white rounded-full w-3 h-3 flex items-center justify-center">
          {order}
        </span>
      )}
    </span>
  );
}

// Portal wrapper for filter popups - renders at body level to escape overflow containers
function FilterPopupPortal({
  children,
  anchorRef,
  isOpen,
  onClickOutside
}: {
  children: React.ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onClickOutside: () => void;
}) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  // Update position when opened
  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen, anchorRef]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClick(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClickOutside();
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClickOutside, anchorRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={popupRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 9999
      }}
    >
      {children}
    </div>,
    document.body
  );
}

// Filterable column header
function FilterableHeader({
  label,
  columnKey,
  filterType,
  value,
  options,
  suggestions,
  dateFrom,
  dateTo,
  onChange,
  onChangeDateFrom,
  onChangeDateTo,
  sortConfigs,
  onSort
}: {
  label: string;
  columnKey: SortColumn;
  filterType: 'text' | 'dropdown' | 'date';
  value?: string;
  options?: string[];
  suggestions?: string[];
  dateFrom?: string;
  dateTo?: string;
  onChange?: (v: string) => void;
  onChangeDateFrom?: (v: string) => void;
  onChangeDateTo?: (v: string) => void;
  sortConfigs?: SortConfig[];
  onSort?: (column: SortColumn, addToMulti: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLTableHeaderCellElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const isFilterActive = filterType === 'date'
    ? Boolean(dateFrom || dateTo)
    : Boolean(value);

  // Find this column's sort config
  const sortIndex = sortConfigs?.findIndex(s => s.column === columnKey) ?? -1;
  const sortConfig = sortIndex >= 0 ? sortConfigs![sortIndex] : undefined;
  const isSorted = sortIndex >= 0;

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onSort && columnKey) {
      // Shift+click to add to multi-sort
      const addToMulti = e.shiftKey;
      onSort(columnKey, addToMulti);
    }
  };

  return (
    <th className="py-2 px-3 font-medium" ref={containerRef}>
      <div className="flex items-center">
        <button
          onMouseDown={handleHeaderClick}
          className="hover:text-blue-400 transition-colors flex items-center select-none"
          title={`Sort by ${label} (Shift+click for multi-sort)`}
        >
          <span>{label}</span>
          <SortIcon
            direction={sortConfig?.direction}
            order={sortConfigs && sortConfigs.length > 1 ? sortIndex + 1 : undefined}
            isActive={isSorted}
          />
        </button>
        <FilterIcon active={isFilterActive} onClick={() => setIsOpen(!isOpen)} />
      </div>
      <FilterPopupPortal anchorRef={containerRef} isOpen={isOpen} onClickOutside={handleClose}>
        {filterType === 'text' && onChange && (
          <TextFilterPopup
            value={value || ''}
            onChange={onChange}
            onClose={handleClose}
            placeholder={label}
            suggestions={suggestions}
          />
        )}
        {filterType === 'dropdown' && options && onChange && (
          <DropdownFilterPopup
            value={value || ''}
            options={options}
            onChange={onChange}
            onClose={handleClose}
          />
        )}
        {filterType === 'date' && onChangeDateFrom && onChangeDateTo && (
          <DateFilterPopup
            dateFrom={dateFrom || ''}
            dateTo={dateTo || ''}
            onChangeFrom={onChangeDateFrom}
            onChangeTo={onChangeDateTo}
            onClose={handleClose}
          />
        )}
      </FilterPopupPortal>
    </th>
  );
}

// Compute stale badge for a ticket
function getStaleBadge(ticket: Ticket): { label: string; classes: string; title: string; pulse?: boolean } | null {
  const tracking = ticket.staleTracking;

  // Check for stale "In Progress" tickets (> 1 day since last activity)
  if (ticket.status === 'In Progress' && ticket.lastActivityDate) {
    const daysSinceActivity = Math.floor(
      (Date.now() - new Date(ticket.lastActivityDate).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceActivity >= 1) {
      return {
        label: `${daysSinceActivity}d`,
        classes: 'bg-green-500/30 text-green-300 border border-green-400/50',
        title: `No activity for ${daysSinceActivity} day${daysSinceActivity !== 1 ? 's' : ''} - needs attention`,
        pulse: true,
      };
    }
  }

  if (!tracking) return null;

  // Snoozed - no pulse, ticket is intentionally paused
  if (tracking.snoozedUntil && new Date(tracking.snoozedUntil) > new Date()) {
    return {
      label: 'Zzz',
      classes: 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30',
      title: `Snoozed until ${new Date(tracking.snoozedUntil).toLocaleDateString()}`,
      pulse: false,
    };
  }

  // Calculate days stale
  const daysStale = Math.floor(
    (Date.now() - new Date(tracking.trackingStartedAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  // Nudged (day 5+ WC) - red, needs attention
  if (tracking.nudgedAt) {
    return {
      label: `${daysStale}d`,
      classes: 'bg-red-500/20 text-red-400 border border-red-500/30',
      title: `Stale ${daysStale}d - Customer nudged`,
      pulse: true,
    };
  }

  // Flagged (day 3+ WC / day 2+ WoU) - amber, needs attention
  if (tracking.flaggedAt) {
    return {
      label: `${daysStale}d`,
      classes: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
      title: `Stale ${daysStale}d - Needs attention`,
      pulse: true,
    };
  }

  return null;
}

// Compute reply badge for a ticket (customer replied, tech hasn't acknowledged)
function getReplyBadge(ticket: Ticket): { label: string; classes: string; title: string } | null {
  if (!ticket.customerRepliedAt) return null;
  const minutesAgo = Math.floor((Date.now() - new Date(ticket.customerRepliedAt).getTime()) / 60000);
  const timeLabel = minutesAgo < 60 ? `${minutesAgo}m` : `${Math.floor(minutesAgo / 60)}h`;
  return {
    label: `Reply ${timeLabel}`,
    classes: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 animate-pulse',
    title: `Customer replied ${timeLabel} ago`,
  };
}

// Compute auto-close badge for a ticket (autoCloseAfterDate set + close pending)
function getAutoCloseBadge(ticket: Ticket): { label: string; classes: string; title: string } | null {
  if (!ticket.autoCloseAfterDate) return null;
  const closeOn = new Date(ticket.autoCloseAfterDate);
  const days = Math.ceil((closeOn.getTime() - Date.now()) / 86400000);
  if (days < 0) return null;  // already past — timer should have closed it
  const warned = !!ticket.autoCloseWarnedAt;
  const dateStr = closeOn.toLocaleDateString();
  if (days === 0) {
    return {
      label: 'Closing',
      classes: 'bg-red-500/30 text-red-300 border border-red-400/50',
      title: `Auto-closing today (${dateStr})`,
    };
  }
  return {
    label: `🔒${days}d`,
    classes: warned
      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
      : 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    title: warned
      ? `Auto-close ${dateStr} (${days}d) — customer warned`
      : `Auto-close ${dateStr} (${days}d)`,
  };
}

function TicketRow({ ticket, onClick, isNewView, onJunk, junkingId, selectMode, isSelected, isSelectable, onToggleSelect, intakeStatus }: { ticket: Ticket; onClick?: () => void; isNewView?: boolean; onJunk?: (ticket: Ticket) => void; junkingId?: number; selectMode?: boolean; isSelected?: boolean; isSelectable?: boolean; onToggleSelect?: (ticket: Ticket) => void; intakeStatus?: string }) {
  const staleBadge = getStaleBadge(ticket);
  const replyBadge = getReplyBadge(ticket);
  const autoCloseBadge = getAutoCloseBadge(ticket);

  // Determine if this row should pulse
  // For "New" view: all unassigned tickets pulse (they all need attention)
  // For other views: tickets with stale badges that have pulse=true, or reply badges
  const shouldPulse = isNewView ? !ticket.assignedResourceId : (staleBadge?.pulse || !!replyBadge);

  // Use inline style for the pulsing box-shadow since Tailwind doesn't have this animation
  const pulseStyle = shouldPulse ? {
    animation: 'row-pulse 2s ease-in-out infinite',
  } : {};

  return (
    <tr
      className={`border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer ${shouldPulse ? 'bg-amber-500/5' : ''}`}
      onClick={onClick}
      style={pulseStyle}
    >
      {selectMode && (
        <td className="py-2 px-2 w-8">
          {isSelectable ? (
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={(e) => { e.stopPropagation(); onToggleSelect?.(ticket); }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
            />
          ) : (
            <span className="w-4 h-4 block" />
          )}
        </td>
      )}
      <td className="py-2 px-3">
        <a
          href={`https://ww6.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=${ticket.autoTaskId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-blue-400 hover:text-blue-300 hover:underline font-mono text-xs"
          title="Open in AutoTask"
        >{ticket.ticketNumber}</a>
      </td>
      <td className="py-2 px-1 text-center">
        {replyBadge && (
          <span
            className={`text-[10px] font-medium px-1 py-0.5 rounded ${replyBadge.classes}`}
            title={replyBadge.title}
          >
            {replyBadge.label}
          </span>
        )}
      </td>
      <td className="py-2 px-1 text-center">
        <div className="flex items-center justify-center gap-1">
          {staleBadge && (
            <span
              className={`text-[10px] font-medium px-1 py-0.5 rounded ${staleBadge.classes}`}
              title={staleBadge.title}
            >
              {staleBadge.label}
            </span>
          )}
          {autoCloseBadge && (
            <span
              className={`text-[10px] font-medium px-1 py-0.5 rounded ${autoCloseBadge.classes}`}
              title={autoCloseBadge.title}
            >
              {autoCloseBadge.label}
            </span>
          )}
        </div>
      </td>
      <td className="py-2 px-1 text-center">
        {intakeStatus && (
          <span
            className={`text-[10px] font-medium px-1 py-0.5 rounded ${
              intakeStatus === 'submitted' ? 'bg-green-500/20 text-green-400' :
              intakeStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
              intakeStatus === 'expired' ? 'bg-slate-500/20 text-slate-400' :
              'bg-blue-500/20 text-blue-400'
            }`}
            title={`Intake form: ${intakeStatus}`}
          >
            {intakeStatus === 'submitted' ? '📋✓' : intakeStatus === 'pending' ? '📋' : '📋✗'}
          </span>
        )}
      </td>
      <td className="py-2 px-3">
        <div className="text-white text-sm truncate max-w-xs" title={ticket.title}>
          {ticket.title}
        </div>
      </td>
      <td className="py-2 px-3 text-slate-300 text-sm">{ticket.companyName || '-'}</td>
      <td className="py-2 px-3 text-slate-400 text-xs truncate max-w-[160px]" title={ticket.contactEmail || ''}>{ticket.contactEmail || '-'}</td>
      <td className="py-2 px-3 text-slate-400 text-xs">{ticket.assignedResourceName || 'Unassigned'}</td>
      <td className="py-2 px-3">
        <span className={`text-xs px-2 py-0.5 rounded ${
          ticket.priority === 'Critical' ? 'bg-red-500/20 text-red-400' :
          ticket.priority === 'High' ? 'bg-orange-500/20 text-orange-400' :
          ticket.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-slate-500/20 text-slate-400'
        }`}>
          {ticket.priority}
        </span>
      </td>
      <td className="py-2 px-3">
        <span className={`text-xs px-2 py-0.5 rounded ${
          ticket.status === 'New' ? 'bg-blue-500/20 text-blue-400' :
          ticket.status === 'In Progress' ? 'bg-green-500/20 text-green-400' :
          ticket.status === 'Waiting Customer' ? 'bg-yellow-500/20 text-yellow-400' :
          ticket.status === 'Waiting On Us' ? 'bg-orange-500/20 text-orange-400' :
          ticket.status === 'Waiting Vendor' ? 'bg-purple-500/20 text-purple-400' :
          ticket.status === 'Scheduled' ? 'bg-cyan-500/20 text-cyan-400' :
          ticket.status === 'Escalated' ? 'bg-red-500/20 text-red-400' :
          'bg-slate-500/20 text-slate-400'
        }`}>
          {ticket.status}
        </span>
      </td>
      <td className="py-2 px-3 text-slate-400 text-xs font-mono">{formatAge(getTicketDate(ticket))}</td>
      {onJunk && (
        <td className="py-2 px-1">
          <button
            onClick={(e) => { e.stopPropagation(); onJunk(ticket); }}
            disabled={junkingId === ticket.autoTaskId}
            className="p-1 text-slate-500 hover:text-red-400 disabled:opacity-50 transition-colors"
            title="Junk - close without action"
          >
            {junkingId === ticket.autoTaskId ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </td>
      )}
    </tr>
  );
}

function StatCard({
  label,
  count,
  color,
  active,
  onClick,
  needsAttention
}: {
  label: string;
  count: number;
  color: string;
  active: boolean;
  onClick: () => void;
  needsAttention?: boolean;
}) {
  const colorClasses: Record<string, string> = {
    slate: active ? 'bg-slate-600 border-slate-500' : 'bg-slate-500/10 border-slate-500/30 hover:bg-slate-500/20',
    red: active ? 'bg-red-600 border-red-500' : 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20',
    green: active ? 'bg-green-600 border-green-500' : 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20',
    yellow: active ? 'bg-yellow-600 border-yellow-500' : 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20',
    orange: active ? 'bg-orange-600 border-orange-500' : 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20',
    blue: active ? 'bg-blue-600 border-blue-500' : 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20',
    teal: active ? 'bg-teal-600 border-teal-500' : 'bg-teal-500/10 border-teal-500/30 hover:bg-teal-500/20',
    violet: active ? 'bg-violet-600 border-violet-500' : 'bg-violet-500/10 border-violet-500/30 hover:bg-violet-500/20',
  };

  // Pulsing ring colors for attention state
  const pulseRingClasses: Record<string, string> = {
    red: 'ring-red-400/50',
    green: 'ring-green-400/50',
    yellow: 'ring-yellow-400/50',
    orange: 'ring-orange-400/50',
    blue: 'ring-blue-400/50',
    teal: 'ring-teal-400/50',
    violet: 'ring-violet-400/50',
    slate: 'ring-slate-400/50',
  };

  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-3 py-1 text-center transition-colors cursor-pointer ${colorClasses[color]} ${
        needsAttention && !active ? `ring-2 ${pulseRingClasses[color]} animate-pulse` : ''
      }`}
    >
      <span className="text-lg font-bold text-white">{count}</span>
      <span className="text-xs text-slate-300 ml-1">{label}</span>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Get initial filter from URL param (default to 'new' on first load)
  const getInitialFilter = (): FilterType => {
    const urlFilter = searchParams.get('filter');
    const validFilters = ['all', 'new', 'in-progress', 'waiting-customer', 'waiting-us', 'project', 'fcr'];
    if (urlFilter && validFilters.includes(urlFilter)) {
      return urlFilter as FilterType;
    }
    return 'new'; // Default to New tickets view
  };

  const [newTickets, setNewTickets] = useState<Ticket[]>([]);
  const [inProgressTickets, setInProgressTickets] = useState<Ticket[]>([]);
  const [waitingCustomer, setWaitingCustomer] = useState<Ticket[]>([]);
  const [waitingUs, setWaitingUs] = useState<Ticket[]>([]);
  const [allFiltered, setAllFiltered] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>(getInitialFilter);

  // Widget stats from API (for consistent counts with UMT-HUD)
  const [widgetStats, setWidgetStats] = useState<WidgetStat[]>([]);

  // Auto-refresh
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Tech filter
  const [resources, setResources] = useState<Resource[]>([]);
  const [hiddenResourceIds, setHiddenResourceIds] = useState<number[]>([]);
  const [selectedTechId, setSelectedTechId] = useState<number | null>(null);

  // Update filter when URL param changes (e.g., from parent iframe navigation)
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    const validFilters = ['all', 'new', 'in-progress', 'waiting-customer', 'waiting-us', 'project', 'fcr'];
    if (urlFilter && validFilters.includes(urlFilter)) {
      setFilter(urlFilter as FilterType);
    }
  }, [searchParams]);

  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    ticketNumber: '',
    title: '',
    client: '',
    contact: '',
    assigned: '',
    priority: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  // Default sort: oldest tickets first (highest age at top)
  const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([{ column: 'age', direction: 'desc' }]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);

  // FCR state
  const [fcrList, setFcrList] = useState<ForwardedRequest[]>([]);
  const [fcrCount, setFcrCount] = useState(0);
  const [loadingFcr, setLoadingFcr] = useState(false);
  const [selectedFcr, setSelectedFcr] = useState<ForwardedRequest | null>(null);
  const [showFcrModal, setShowFcrModal] = useState(false);
  const [fcrFilter, setFcrFilter] = useState<'action' | 'ignored' | 'completed' | 'all'>('action');

  // Junk ticket state
  const [junkingTicketId, setJunkingTicketId] = useState<number | null>(null);

  // Bulk selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);
  const [intakeStatusMap, setIntakeStatusMap] = useState<Record<number, string>>({});
  const [isBulkMerging, setIsBulkMerging] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const primaryTicket = selectedTickets[0] || null;
  // For merge: all selected must be same company
  const allSameCompany = selectedTickets.length > 0 && selectedTickets.every(t => t.companyId === selectedTickets[0].companyId);

  // Auto-exit select mode when switching tabs
  useEffect(() => {
    setSelectMode(false);
    setSelectedTickets([]);
  }, [filter]);

  const handleJunkTicket = async (ticket: Ticket) => {
    if (!confirm(`Close "${ticket.ticketNumber} - ${ticket.title}" as junk?`)) return;
    setJunkingTicketId(ticket.autoTaskId);
    try {
      await updateTicket(ticket.autoTaskId, { status: 5, resolution: 'Junk - closed without action' });
      // Optimistic removal from all lists
      const removeTicket = (prev: Ticket[]) => prev.filter(t => t.autoTaskId !== ticket.autoTaskId);
      setNewTickets(removeTicket);
      setInProgressTickets(removeTicket);
      setWaitingCustomer(removeTicket);
      setWaitingUs(removeTicket);
      setAllFiltered(removeTicket);
    } catch (err) {
      console.error('Failed to junk ticket:', err);
      alert('Failed to close ticket: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setJunkingTicketId(null);
    }
  };

  // Toggle ticket selection for bulk merge
  const handleToggleSelect = (ticket: Ticket) => {
    setSelectedTickets(prev => {
      const exists = prev.find(t => t.autoTaskId === ticket.autoTaskId);
      if (exists) {
        return prev.filter(t => t.autoTaskId !== ticket.autoTaskId);
      }
      return [...prev, ticket];
    });
  };

  // Exit selection mode
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedTickets([]);
  };

  // Bulk merge handler
  const handleBulkMerge = async () => {
    if (selectedTickets.length < 2 || !primaryTicket) return;

    const secondaryTickets = selectedTickets.slice(1);
    const ticketList = secondaryTickets.map(t => t.ticketNumber).join(', ');
    if (!confirm(`Merge ${secondaryTickets.length} ticket(s) (${ticketList}) into ${primaryTicket.ticketNumber}?`)) return;

    setIsBulkMerging(true);
    try {
      for (const t of secondaryTickets) {
        await mergeTicket(primaryTicket.autoTaskId, t.autoTaskId);
      }
      exitSelectMode();
      fetchData(false);
    } catch (err) {
      alert('Merge failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsBulkMerging(false);
    }
  };

  // Bulk mark as project
  const handleBulkProject = async (isProject: boolean) => {
    setIsBulkUpdating(true);
    try {
      for (const t of selectedTickets) {
        await toggleProjectFlag(t.autoTaskId, isProject);
      }
      exitSelectMode();
      fetchData(false);
    } catch (err) {
      alert('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Bulk status change
  const handleBulkStatusChange = async (statusId: number, statusLabel: string) => {
    if (!confirm(`Change ${selectedTickets.length} ticket(s) to "${statusLabel}"?`)) return;
    setIsBulkUpdating(true);
    try {
      for (const t of selectedTickets) {
        await updateTicket(t.autoTaskId, { status: statusId });
      }
      exitSelectMode();
      fetchData(false);
    } catch (err) {
      alert('Status change failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Handle sort column click (supports multi-column with Shift)
  const handleSort = (column: SortColumn, addToMulti: boolean) => {
    setSortConfigs(prev => {
      const existingIndex = prev.findIndex(s => s.column === column);

      if (existingIndex >= 0) {
        // Column already in sort - toggle direction or remove
        const existing = prev[existingIndex];
        if (existing.direction === 'asc') {
          // Toggle to desc
          const updated = [...prev];
          updated[existingIndex] = { ...existing, direction: 'desc' };
          return updated;
        } else {
          // Remove from sort
          return prev.filter((_, i) => i !== existingIndex);
        }
      } else {
        // Add new sort
        const newSort: SortConfig = { column, direction: 'asc' };
        if (addToMulti) {
          // Add to existing sorts
          return [...prev, newSort];
        } else {
          // Replace all sorts with this one
          return [newSort];
        }
      }
    });
  };

  // Fetch data function - extracted for reuse in initial load and refresh
  const fetchData = useCallback(async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      // Queue IDs - same as widget-summary uses
      const LEVEL_I_SUPPORT_QUEUE_ID = 29682833;
      const DATTO_RMM_ALERT_QUEUE_ID = 8;
      const QUEUE_IDS = [LEVEL_I_SUPPORT_QUEUE_ID, DATTO_RMM_ALERT_QUEUE_ID];

      // Fetch tickets using server-side filtering (same as widget-summary)
      // This ensures ticket lists match the widget-summary counts exactly
      const [
        unassignedData,
        inProgressData,
        waitCustData,
        waitUsData,
        allAssignedData,
        widgetSummary
      ] = await Promise.all([
        // "New" = ALL unassigned tickets in Level I Support queue (any status except Complete/Closed)
        // Matches AutoTask's "New tickets" view: unassigned, not complete
        // QuickSync keeps cache fresh every minute (including recently-completed tickets)
        getTickets({ status: 'New,In Progress,Waiting Customer,Waiting On Us,Waiting Vendor,Scheduled,Escalated', unassigned: true, queueId: LEVEL_I_SUPPORT_QUEUE_ID, limit: 500 }),
        // "In Progress" = assigned, status="In Progress", in allowed queues (cached is fine)
        getTickets({ status: 'In Progress', queueId: QUEUE_IDS, limit: 500 }),
        // "Waiting Customer" = assigned, status="Waiting Customer", in allowed queues (cached is fine)
        getTickets({ status: 'Waiting Customer', queueId: QUEUE_IDS, limit: 500 }),
        // "Waiting On Us" = assigned, status="Waiting On Us", in allowed queues (cached is fine)
        getTickets({ status: 'Waiting On Us', queueId: QUEUE_IDS, limit: 500 }),
        // "All" = all statuses except excluded, in allowed queues (for table display)
        getTickets({ status: 'New,In Progress,Waiting Customer,Waiting On Us,Waiting Vendor,Scheduled,Escalated', queueId: QUEUE_IDS, limit: 500 }),
        // Get widget stats from API for consistent counts with UMT-HUD
        getWidgetSummary(),
      ]);

      // Store widget stats for stat card counts
      setWidgetStats(widgetSummary.stats);

      // Filter out complete/closed from unassigned
      const needsAttention = unassignedData.filter(
        t => t.status !== 'Complete' && t.status !== 'Closed'
      );

      // Filter to only assigned tickets (API returns assigned due to how the query works)
      const inProgressFiltered = inProgressData.filter(t => t.assignedResourceId);
      const waitCustFiltered = waitCustData.filter(t => t.assignedResourceId);
      const waitUsFiltered = waitUsData.filter(t => t.assignedResourceId);
      const allFilteredData = allAssignedData.filter(t => t.assignedResourceId);

      console.log('=== Dashboard Data (server-side filtered) ===');
      console.log('New:', needsAttention.length, 'Progress:', inProgressFiltered.length,
        'WaitCust:', waitCustFiltered.length, 'WaitUs:', waitUsFiltered.length, 'All:', allFilteredData.length);

      setNewTickets(needsAttention);
      setInProgressTickets(inProgressFiltered);
      setWaitingCustomer(waitCustFiltered);
      setWaitingUs(waitUsFiltered);
      setAllFiltered(allFilteredData);

      // Load intake status summary (non-blocking)
      getIntakeStatusSummary().then(setIntakeStatusMap).catch(() => {});

      // Load resources for tech filter (only on initial load)
      if (isInitial) {
        try {
          const [resourcesData, adminConfig] = await Promise.all([
            getResources(),
            getAdminConfig().catch(() => ({ admins: [], contractors: [], hiddenResourceIds: [] })),
          ]);
          // Filter to only actual technicians (not API users)
          const filteredResources = resourcesData.filter(r =>
            r.isActive &&
            r.licenseType !== 3 &&
            r.autoTaskId !== 4 &&
            !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
          );
          setResources(filteredResources);
          setHiddenResourceIds(adminConfig.hiddenResourceIds || []);
        } catch (err) {
          console.error('Failed to load resources:', err);
        }
      }
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchData(true);
    // Also load FCR count for the stat card
    getFCRCount().then(setFcrCount).catch(() => setFcrCount(0));
  }, [fetchData]);

  // Auto-refresh every 60 seconds (background safety net — SignalR delivers
  // near-real-time updates, but we keep this as a fallback in case the
  // WebSocket drops or a change-feed event is missed)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
      // Also refresh FCR count
      getFCRCount().then(setFcrCount).catch(() => {});
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [fetchData]);

  // Real-time updates via SignalR — refetches the ticket list when any ticket
  // changes (assignment / status / activity). Debounced so a burst of changes
  // from a sync run only triggers one refetch, not 30.
  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;
    realtime.connect();
    const unsub = realtime.on<TicketChangedPayload>('ticket-changed', () => {
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        fetchData(false);
      }, 750); // 750ms debounce — feels instant to the user, coalesces bursts
    });
    return () => {
      unsub();
      if (pending) clearTimeout(pending);
    };
  }, [fetchData]);

  // Load FCR list when FCR filter is selected or fcrFilter changes
  useEffect(() => {
    if (filter === 'fcr') {
      setLoadingFcr(true);

      // Determine which statuses to fetch based on fcrFilter
      let fetchPromise: Promise<ForwardedRequest[]>;

      if (fcrFilter === 'action') {
        // Fetch both pending and error FCRs (needs action)
        fetchPromise = Promise.all([
          getFCRList({ status: 'pending' }),
          getFCRList({ status: 'error' }),
        ]).then(([pending, errors]) => [...errors, ...pending]); // Errors first
      } else if (fcrFilter === 'ignored') {
        fetchPromise = getFCRList({ status: 'ignored' });
      } else if (fcrFilter === 'completed') {
        fetchPromise = getFCRList({ status: 'completed' });
      } else {
        // 'all' - fetch all statuses
        fetchPromise = Promise.all([
          getFCRList({ status: 'pending' }),
          getFCRList({ status: 'error' }),
          getFCRList({ status: 'ignored' }),
          getFCRList({ status: 'completed' }),
        ]).then(([pending, errors, ignored, completed]) => [...errors, ...pending, ...ignored, ...completed]);
      }

      fetchPromise
        .then(setFcrList)
        .catch(err => {
          console.error('Failed to load FCRs:', err);
          setFcrList([]);
        })
        .finally(() => setLoadingFcr(false));
    }
  }, [filter, fcrFilter]);

  // Get unique values for column filter dropdowns
  const priorityOptions = ['Critical', 'High', 'Medium', 'Low'];
  const statusOptions = ['New', 'In Progress', 'Waiting Customer', 'Waiting On Us', 'Waiting Vendor', 'Scheduled', 'Escalated'];

  // Apply column filters to a ticket list (optionally exclude certain filters for dropdown options)
  const applyColumnFilters = (tickets: Ticket[], excludeFilter?: 'client'): Ticket[] => {
    return tickets.filter(ticket => {
      // Text filters (case-insensitive contains)
      if (columnFilters.ticketNumber && !ticket.ticketNumber.toLowerCase().includes(columnFilters.ticketNumber.toLowerCase())) return false;
      if (columnFilters.title && !ticket.title.toLowerCase().includes(columnFilters.title.toLowerCase())) return false;
      if (excludeFilter !== 'client' && columnFilters.client && !(ticket.companyName || '').toLowerCase().includes(columnFilters.client.toLowerCase())) return false;
      if (columnFilters.contact && !(ticket.contactEmail || '').toLowerCase().includes(columnFilters.contact.toLowerCase())) return false;

      // Dropdown filters (exact match)
      if (columnFilters.assigned) {
        const assignedName = ticket.assignedResourceName || 'Unassigned';
        if (assignedName !== columnFilters.assigned) return false;
      }
      if (columnFilters.priority && ticket.priority !== columnFilters.priority) return false;
      if (columnFilters.status && ticket.status !== columnFilters.status) return false;

      // Date range filter
      if (columnFilters.dateFrom || columnFilters.dateTo) {
        const ticketDate = getTicketDate(ticket);
        if (!ticketDate) return false;
        const date = new Date(ticketDate);
        if (columnFilters.dateFrom && date < new Date(columnFilters.dateFrom)) return false;
        if (columnFilters.dateTo && date > new Date(columnFilters.dateTo + 'T23:59:59')) return false;
      }

      return true;
    });
  };

  // Get sort value for a ticket by column
  const getSortValue = (ticket: Ticket, column: SortColumn): string | number => {
    switch (column) {
      case 'ticketNumber':
        return ticket.ticketNumber;
      case 'title':
        return ticket.title.toLowerCase();
      case 'client':
        return (ticket.companyName || '').toLowerCase();
      case 'contact':
        return (ticket.contactEmail || '').toLowerCase();
      case 'assigned':
        return (ticket.assignedResourceName || 'zzz').toLowerCase();
      case 'created':
        return new Date(getTicketDate(ticket) || 0).getTime();
      case 'priority':
        const priorityOrder: Record<string, number> = { Critical: 1, High: 2, Medium: 3, Low: 4, None: 5 };
        return priorityOrder[ticket.priority] || 99;
      case 'status':
        const statusOrder: Record<string, number> = {
          'New': 1, 'In Progress': 2, 'Waiting On Us': 3, 'Waiting Customer': 4,
          'Waiting Vendor': 5, 'Scheduled': 6, 'Escalated': 7, 'Complete': 8, 'Closed': 9
        };
        return statusOrder[ticket.status] || 99;
      case 'age':
        // Age is inverse of created time - older = higher age value
        return new Date(getTicketDate(ticket) || 0).getTime();
      default:
        return '';
    }
  };

  // Sort tickets by multiple columns
  const applySorting = (tickets: Ticket[]): Ticket[] => {
    if (sortConfigs.length === 0) return tickets;

    return [...tickets].sort((a, b) => {
      for (const config of sortConfigs) {
        const aVal = getSortValue(a, config.column);
        const bVal = getSortValue(b, config.column);

        if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Get base tickets based on status filter (before column filters)
  const getBaseTickets = (): Ticket[] => {
    let tickets: Ticket[];
    switch (filter) {
      case 'new':
        tickets = newTickets;
        break;
      case 'in-progress':
        // Exclude project tickets from normal status views
        tickets = inProgressTickets.filter(t => !t.isProject);
        break;
      case 'waiting-customer':
        tickets = waitingCustomer.filter(t => !t.isProject);
        break;
      case 'waiting-us':
        tickets = waitingUs.filter(t => !t.isProject);
        break;
      case 'project':
        // Show all project tickets from allFiltered (any status)
        tickets = allFiltered.filter(t => t.isProject);
        break;
      default:
        // "All" view - uses properly filtered allFiltered state, sorted by date (excludes projects)
        tickets = [...allFiltered].filter(t => !t.isProject).sort((a, b) => {
          const dateA = new Date(getTicketDate(a) || 0).getTime();
          const dateB = new Date(getTicketDate(b) || 0).getTime();
          return dateB - dateA;
        });
    }

    // Apply tech filter if selected (but NOT for "New" view - those are always unassigned)
    if (selectedTechId && filter !== 'new') {
      tickets = tickets.filter(t => t.assignedResourceId === selectedTechId);
    }

    return tickets;
  };

  const getFilteredTickets = (): Ticket[] => {
    const tickets = getBaseTickets();

    // Apply column filters
    let result = applyColumnFilters(tickets);

    // Apply sorting
    result = applySorting(result);

    return result;
  };

  const filteredTickets = getFilteredTickets();

  // Build companyId -> ticket count map for Field Map widget (so map matches table exactly)
  const companyTickets = useMemo(() => {
    const map = new Map<number, number>();
    for (const t of filteredTickets) {
      if (t.companyId) map.set(t.companyId, (map.get(t.companyId) || 0) + 1);
    }
    return map;
  }, [filteredTickets]);

  // Get client options - apply all filters EXCEPT client filter so user can change selection
  // Filter out invalid "Company XXX" entries (tickets with company IDs that don't exist in AutoTask)
  const clientOptionsFromFiltered = [...new Set(
    applyColumnFilters(getBaseTickets(), 'client')
      .map(t => t.companyName)
      .filter((n): n is string => Boolean(n))
      .filter(n => !/^Company \d+$/.test(n))
  )].sort();

  // Check if any column filters are active
  const hasColumnFilters = Object.values(columnFilters).some(v => v !== '');

  // Check if any filters/sort are non-default
  const hasAnyFilters = hasColumnFilters ||
    filter !== 'new' ||
    selectedTechId !== null ||
    sortConfigs.length !== 1 ||
    sortConfigs[0]?.column !== 'age' ||
    sortConfigs[0]?.direction !== 'desc';

  // Clear all column filters
  const clearColumnFilters = () => {
    setColumnFilters({
      ticketNumber: '',
      title: '',
      client: '',
      contact: '',
      assigned: '',
      priority: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  // Reset everything to defaults
  const resetToDefaults = () => {
    clearColumnFilters();
    setFilter('new');
    setSelectedTechId(null);
    setSortConfigs([{ column: 'age', direction: 'desc' }]);
  };

  // Helper to filter tickets by selected tech
  const filterByTech = (tickets: Ticket[]) => {
    if (!selectedTechId) return tickets;
    return tickets.filter(t => t.assignedResourceId === selectedTechId);
  };

  // Get widget stat value by label (from API)
  const getStatValue = (label: string): number => {
    const stat = widgetStats.find(s => s.label === label);
    return typeof stat?.value === 'number' ? stat.value : 0;
  };

  // Stat card counts: use API counts when no tech filter, otherwise use client-side filtered
  // When tech filter is selected, we fall back to client-side filtering since API doesn't support per-tech
  // Project tickets are excluded from Progress/WaitUs/WaitCust/All counts
  const newCount = getStatValue('New') || newTickets.length; // New/unassigned always shows total from API
  const projectCount = selectedTechId
    ? filterByTech(allFiltered).filter(t => t.isProject).length
    : getStatValue('Projects') || allFiltered.filter(t => t.isProject).length;
  const inProgressCount = selectedTechId
    ? filterByTech(inProgressTickets).filter(t => !t.isProject).length
    : Math.max(0, getStatValue('Progress') - allFiltered.filter(t => t.isProject && t.status === 'In Progress').length);
  const waitUsCount = selectedTechId
    ? filterByTech(waitingUs).filter(t => !t.isProject).length
    : Math.max(0, getStatValue('Wait Us') - allFiltered.filter(t => t.isProject && t.status === 'Waiting On Us').length);
  const waitCustCount = selectedTechId
    ? filterByTech(waitingCustomer).filter(t => !t.isProject).length
    : Math.max(0, getStatValue('Wait Cust') - allFiltered.filter(t => t.isProject && t.status === 'Waiting Customer').length);
  // All count = exactly what shows in the "All" view (allFiltered = assigned tickets, excluding projects)
  const allCount = selectedTechId
    ? filterByTech(allFiltered).filter(t => !t.isProject).length
    : allFiltered.filter(t => !t.isProject).length;

  // Compute which filters need attention (have stale tickets that should pulse)
  const hasStaleInProgress = (selectedTechId ? filterByTech(inProgressTickets) : inProgressTickets)
    .filter(t => !t.isProject)
    .some(t => getStaleBadge(t)?.pulse);
  const hasStaleWaitUs = (selectedTechId ? filterByTech(waitingUs) : waitingUs)
    .filter(t => !t.isProject)
    .some(t => getStaleBadge(t) !== null);
  const hasStaleWaitCust = (selectedTechId ? filterByTech(waitingCustomer) : waitingCustomer)
    .filter(t => !t.isProject)
    .some(t => getStaleBadge(t) !== null);

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        userEmail={user?.email}
        onNewTicket={() => setShowNewTicketModal(true)}
        middleContent={
          !loading && !error ? (
            <div className="flex items-center space-x-2">
              <StatCard
                label="FCR"
                count={fcrCount}
                color="violet"
                active={filter === 'fcr'}
                onClick={() => { setFilter('fcr'); clearColumnFilters(); }}
                needsAttention={fcrCount > 0}
              />
              <StatCard
                label="New"
                count={newCount}
                color="red"
                active={filter === 'new'}
                onClick={() => { setFilter('new'); clearColumnFilters(); }}
                needsAttention={newCount > 0}
              />
              <StatCard
                label="Progress"
                count={inProgressCount}
                color="green"
                active={filter === 'in-progress'}
                onClick={() => { setFilter('in-progress'); clearColumnFilters(); }}
                needsAttention={hasStaleInProgress}
              />
              <StatCard
                label="Wait Us"
                count={waitUsCount}
                color="orange"
                active={filter === 'waiting-us'}
                onClick={() => { setFilter('waiting-us'); clearColumnFilters(); }}
                needsAttention={hasStaleWaitUs}
              />
              <StatCard
                label="Wait Cust"
                count={waitCustCount}
                color="yellow"
                active={filter === 'waiting-customer'}
                onClick={() => { setFilter('waiting-customer'); clearColumnFilters(); }}
                needsAttention={hasStaleWaitCust}
              />
              <StatCard
                label="All"
                count={allCount}
                color="blue"
                active={filter === 'all'}
                onClick={() => { setFilter('all'); clearColumnFilters(); }}
              />
              <StatCard
                label="Projects"
                count={projectCount}
                color="teal"
                active={filter === 'project'}
                onClick={() => { setFilter('project'); clearColumnFilters(); }}
              />
            </div>
          ) : undefined
        }
      />

      {/* Stale Audit — collapsed by default; expands to show tickets stuck past their
          expected nudge / auto-close tier (introduced after Ed's 6/9 report about
          tickets sitting in Waiting Customer for weeks without nudges firing). */}
      {!loading && !error && filter !== 'fcr' && (
        <StaleAuditPanel
          onOpenTicket={(autoTaskId) => {
            const all = [...newTickets, ...inProgressTickets, ...waitingCustomer, ...waitingUs];
            const ticket = all.find(t => t.autoTaskId === autoTaskId);
            if (ticket) {
              setSelectedTicket(ticket);
              setShowTicketModal(true);
            }
          }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading tickets...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* FCR List */}
      {!loading && !error && filter === 'fcr' && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden mt-4">
          <div className="px-4 py-2 border-b border-slate-700 bg-slate-700/50 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <span className="text-sm text-white font-medium">Forwarded Customer Requests</span>
              {/* FCR Sub-filters */}
              <div className="flex gap-1">
                <button
                  onClick={() => setFcrFilter('action')}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    fcrFilter === 'action'
                      ? 'bg-violet-500 text-white'
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  Action Needed
                </button>
                <button
                  onClick={() => setFcrFilter('ignored')}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    fcrFilter === 'ignored'
                      ? 'bg-slate-500 text-white'
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  Ignored
                </button>
                <button
                  onClick={() => setFcrFilter('completed')}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    fcrFilter === 'completed'
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setFcrFilter('all')}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    fcrFilter === 'all'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  All
                </button>
              </div>
            </div>
            <span className="text-xs text-slate-400">
              {fcrList.length} {fcrList.length === 1 ? 'request' : 'requests'}
            </span>
          </div>
          <div className="p-4">
            <FCRList
              fcrs={fcrList}
              loading={loadingFcr}
              onFCRClick={(fcr) => {
                setSelectedFcr(fcr);
                setShowFcrModal(true);
              }}
              emptyMessage={
                fcrFilter === 'action' ? undefined : // Use default message for action
                fcrFilter === 'ignored' ? 'No ignored requests' :
                fcrFilter === 'completed' ? 'No completed requests' :
                'No forwarded requests'
              }
            />
          </div>
        </div>
      )}

      {/* Ticket Table */}
      {!loading && !error && filter !== 'fcr' && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden mt-4">
          {/* Table hints and filter controls */}
          <div className="px-4 py-2 border-b border-slate-700 bg-slate-700/50 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-slate-500">
                Tip: Click column to sort • Shift+click for multi-sort
              </span>
              {/* Tech Filter Dropdown */}
              <select
                value={selectedTechId || ''}
                onChange={(e) => setSelectedTechId(e.target.value ? parseInt(e.target.value) : null)}
                className="bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1 focus:border-blue-500 focus:outline-none"
              >
                <option value="">All Techs</option>
                {resources
                  .filter(r => !hiddenResourceIds.includes(r.autoTaskId))
                  .map(r => (
                    <option key={r.autoTaskId} value={r.autoTaskId}>
                      {r.firstName} {r.lastName}
                    </option>
                  ))}
              </select>
              {/* Record count */}
              <span className="text-xs text-slate-400">
                {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Last updated and refresh button */}
              <div className="flex items-center gap-2">
                {lastUpdated && (
                  <span className="text-[10px] text-slate-500">
                    Updated {formatTimeAgo(lastUpdated)}
                  </span>
                )}
                <button
                  onClick={() => fetchData(false)}
                  disabled={isRefreshing}
                  className={`text-xs px-2 py-0.5 rounded border ${
                    isRefreshing
                      ? 'text-slate-500 border-slate-600 cursor-not-allowed'
                      : 'text-blue-400 border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500/50'
                  }`}
                  title="Refresh tickets (auto-refreshes every 60s)"
                >
                  {isRefreshing ? (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Refreshing
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </span>
                  )}
                </button>
              </div>
              <button
                onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
                className={`text-xs px-2 py-0.5 rounded border ${
                  selectMode
                    ? 'text-red-400 border-red-500/30 hover:bg-red-500/10'
                    : 'text-slate-400 border-slate-600 hover:bg-slate-600 hover:text-white'
                }`}
              >
                {selectMode ? 'Cancel' : 'Select'}
              </button>
              {hasAnyFilters && (
                <button
                  onClick={resetToDefaults}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
          {filteredTickets.length === 0 ? (
            <div className="p-4 text-slate-400 text-sm text-center">No tickets found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" key={`table-${columnFilters.client}-${columnFilters.assigned}-${columnFilters.priority}-${filter}`}>
                <thead className="bg-slate-700">
                  <tr className="text-left text-xs text-slate-400">
                    {selectMode && (
                      <th className="py-2 px-2 w-8">
                        <input
                          type="checkbox"
                          checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                          onChange={() => {
                            if (selectedTickets.length === filteredTickets.length) {
                              setSelectedTickets([]);
                            } else {
                              setSelectedTickets([...filteredTickets]);
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                          title="Select all"
                        />
                      </th>
                    )}
                    <FilterableHeader
                      label="Ticket #"
                      columnKey="ticketNumber"
                      filterType="text"
                      value={columnFilters.ticketNumber}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, ticketNumber: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <th className="py-2 px-1 w-16 text-center">
                      <span className="group relative inline-flex items-center gap-0.5">
                        Reply
                        <span className="text-slate-500 cursor-help" title="Time since customer replied. Badge clears when ticket is opened.">ⓘ</span>
                      </span>
                    </th>
                    <th className="py-2 px-1 w-14 text-center">
                      <span className="group relative inline-flex items-center gap-0.5">
                        Stale
                        <span className="text-slate-500 cursor-help" title="Days in Waiting Customer or Waiting On Us status without activity.">ⓘ</span>
                      </span>
                    </th>
                    <th className="py-2 px-1 w-14 text-center">
                      <span className="group relative inline-flex items-center gap-0.5">
                        Intake
                        <span className="text-slate-500 cursor-help" title="Status of the intake form sent to the end user.">ⓘ</span>
                      </span>
                    </th>
                    <FilterableHeader
                      label="Title"
                      columnKey="title"
                      filterType="text"
                      value={columnFilters.title}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, title: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <FilterableHeader
                      label="Client"
                      columnKey="client"
                      filterType="text"
                      value={columnFilters.client}
                      suggestions={clientOptionsFromFiltered}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, client: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <FilterableHeader
                      label="Contact"
                      columnKey="contact"
                      filterType="text"
                      value={columnFilters.contact}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, contact: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <th
                      className="px-3 py-2 cursor-pointer select-none hover:bg-slate-600/50"
                      onClick={(e) => handleSort('assigned', e.shiftKey)}
                    >
                      <span className="flex items-center gap-1">
                        Assigned
                        {sortConfigs.find(s => s.column === 'assigned') && (
                          <span className="text-blue-400">
                            {sortConfigs.find(s => s.column === 'assigned')?.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </span>
                    </th>
                    <FilterableHeader
                      label="Priority"
                      columnKey="priority"
                      filterType="dropdown"
                      value={columnFilters.priority}
                      options={priorityOptions}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, priority: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <FilterableHeader
                      label="Status"
                      columnKey="status"
                      filterType="dropdown"
                      value={columnFilters.status}
                      options={statusOptions}
                      onChange={(v) => setColumnFilters(prev => ({ ...prev, status: v }))}
                      sortConfigs={sortConfigs}
                      onSort={handleSort}
                    />
                    <th
                      className="px-3 py-2 cursor-pointer select-none hover:bg-slate-600/50"
                      onClick={(e) => handleSort('age', e.shiftKey)}
                    >
                      <span className="flex items-center gap-1">
                        Age
                        {sortConfigs.find(s => s.column === 'age') && (
                          <span className="text-blue-400">
                            {sortConfigs.find(s => s.column === 'age')?.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </span>
                    </th>
                    {filter === 'new' && <th className="py-2 px-1 w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket) => (
                    <TicketRow
                      key={ticket.id || ticket.ticketNumber}
                      ticket={ticket}
                      isNewView={filter === 'new'}
                      onJunk={filter === 'new' ? handleJunkTicket : undefined}
                      junkingId={junkingTicketId ?? undefined}
                      selectMode={selectMode}
                      isSelected={selectedTickets.some(t => t.autoTaskId === ticket.autoTaskId)}
                      isSelectable={true}
                      onToggleSelect={handleToggleSelect}
                      intakeStatus={intakeStatusMap[ticket.autoTaskId]}
                      onClick={() => {
                        setSelectedTicket(ticket);
                        setShowTicketModal(true);
                        // Acknowledge customer reply badge when opening ticket
                        if (ticket.customerRepliedAt && ticket.autoTaskId) {
                          acknowledgeTicketReply(ticket.autoTaskId).then(() => {
                            // Clear the badge locally so it disappears on next render
                            const updateTicket = (prev: Ticket[]) =>
                              prev.map(t => t.autoTaskId === ticket.autoTaskId ? { ...t, customerRepliedAt: undefined } : t);
                            setNewTickets(updateTicket);
                            setInProgressTickets(updateTicket);
                            setWaitingCustomer(updateTicket);
                            setWaitingUs(updateTicket);
                            setAllFiltered(updateTicket);
                          }).catch(() => { /* silent - non-critical */ });
                        }
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bulk Merge Action Bar */}
      {selectMode && selectedTickets.length >= 1 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-600 rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3 z-50">
          <span className="text-sm text-slate-300">
            <span className="text-white font-medium">{selectedTickets.length}</span> selected
          </span>
          <div className="w-px h-6 bg-slate-600" />
          {/* Merge - requires 2+ tickets from same company */}
          <button
            onClick={handleBulkMerge}
            disabled={isBulkMerging || isBulkUpdating || selectedTickets.length < 2 || !allSameCompany}
            className="px-3 py-1.5 bg-purple-600/20 text-purple-300 border border-purple-500/50 hover:bg-purple-600/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium rounded transition-colors"
            title={selectedTickets.length < 2 ? 'Select 2+ tickets' : !allSameCompany ? 'All tickets must be same client' : `Merge into ${primaryTicket?.ticketNumber}`}
          >
            {isBulkMerging ? 'Merging...' : 'Merge'}
          </button>
          {/* Mark as Project */}
          <button
            onClick={() => handleBulkProject(true)}
            disabled={isBulkUpdating || isBulkMerging}
            className="px-3 py-1.5 bg-teal-600/20 text-teal-300 border border-teal-500/50 hover:bg-teal-600/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium rounded transition-colors"
          >
            {isBulkUpdating ? '...' : 'Mark Project'}
          </button>
          {/* Unmark Project */}
          {selectedTickets.some(t => t.isProject) && (
            <button
              onClick={() => handleBulkProject(false)}
              disabled={isBulkUpdating || isBulkMerging}
              className="px-3 py-1.5 bg-slate-600/20 text-slate-300 border border-slate-500/50 hover:bg-slate-600/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium rounded transition-colors"
            >
              Unmark Project
            </button>
          )}
          {/* Status changes */}
          <div className="relative group">
            <button
              disabled={isBulkUpdating || isBulkMerging}
              className="px-3 py-1.5 bg-blue-600/20 text-blue-300 border border-blue-500/50 hover:bg-blue-600/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium rounded transition-colors"
            >
              Change Status
            </button>
            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-slate-800 border border-slate-600 rounded-lg shadow-xl min-w-[160px] py-1">
              {[
                { id: 1, label: 'New' },
                { id: 8, label: 'In Progress' },
                { id: 7, label: 'Waiting Customer' },
                { id: 12, label: 'Waiting On Us' },
                { id: 5, label: 'Complete' },
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => handleBulkStatusChange(s.id, s.label)}
                  className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 transition-colors"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="w-px h-6 bg-slate-600" />
          <button
            onClick={exitSelectMode}
            className="text-slate-400 hover:text-white text-xs"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Field Map Widget */}
      {!loading && !error && filter !== 'fcr' && (
        <FieldMapWidget filter={filter} companyTickets={companyTickets} />
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={showTicketModal}
        onClose={() => {
          setShowTicketModal(false);
          setSelectedTicket(null);
        }}
        onTicketCompleted={(ticketId: string) => {
          // Optimistic update: immediately remove completed ticket from all lists
          setNewTickets(prev => prev.filter(t => t.id !== ticketId));
          setInProgressTickets(prev => prev.filter(t => t.id !== ticketId));
          setWaitingCustomer(prev => prev.filter(t => t.id !== ticketId));
          setWaitingUs(prev => prev.filter(t => t.id !== ticketId));
          setAllFiltered(prev => prev.filter(t => t.id !== ticketId));
        }}
        onTicketUpdated={async () => {
          // Refresh ticket data after update using server-side filtering
          try {
            const QUEUE_IDS = [29682833, 8];
            const [
              unassignedData,
              inProgressData,
              waitCustData,
              waitUsData,
              allAssignedData,
              widgetSummary
            ] = await Promise.all([
              getTickets({ status: 'New,In Progress,Waiting Customer,Waiting On Us,Waiting Vendor,Scheduled,Escalated', unassigned: true, queueId: 29682833, limit: 500 }),
              getTickets({ status: 'In Progress', queueId: QUEUE_IDS, limit: 500 }),
              getTickets({ status: 'Waiting Customer', queueId: QUEUE_IDS, limit: 500 }),
              getTickets({ status: 'Waiting On Us', queueId: QUEUE_IDS, limit: 500 }),
              getTickets({ status: 'New,In Progress,Waiting Customer,Waiting On Us,Waiting Vendor,Scheduled,Escalated', queueId: QUEUE_IDS, limit: 500 }),
              getWidgetSummary(),
            ]);
            setWidgetStats(widgetSummary.stats);
            setNewTickets(unassignedData.filter((t: Ticket) => t.status !== 'Complete' && t.status !== 'Closed'));
            setInProgressTickets(inProgressData.filter((t: Ticket) => t.assignedResourceId));
            setWaitingCustomer(waitCustData.filter((t: Ticket) => t.assignedResourceId));
            setWaitingUs(waitUsData.filter((t: Ticket) => t.assignedResourceId));
            setAllFiltered(allAssignedData.filter((t: Ticket) => t.assignedResourceId));
          } catch (err) {
            console.error('Failed to refresh tickets:', err);
          }
        }}
      />

      {/* New Ticket Modal */}
      <NewTicketModal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onTicketCreated={() => {
          // Refresh ticket data after creating a new ticket
          fetchData(false);
        }}
      />

      {/* FCR Process Modal */}
      <FCRProcessModal
        isOpen={showFcrModal}
        fcr={selectedFcr}
        onClose={() => {
          setShowFcrModal(false);
          setSelectedFcr(null);
        }}
        onProcessed={() => {
          // Refresh FCR list and count after processing
          getFCRCount().then(setFcrCount).catch(() => {});

          // Refresh based on current fcrFilter
          let fetchPromise: Promise<ForwardedRequest[]>;
          if (fcrFilter === 'action') {
            fetchPromise = Promise.all([
              getFCRList({ status: 'pending' }),
              getFCRList({ status: 'error' }),
            ]).then(([pending, errors]) => [...errors, ...pending]);
          } else if (fcrFilter === 'ignored') {
            fetchPromise = getFCRList({ status: 'ignored' });
          } else if (fcrFilter === 'completed') {
            fetchPromise = getFCRList({ status: 'completed' });
          } else {
            fetchPromise = Promise.all([
              getFCRList({ status: 'pending' }),
              getFCRList({ status: 'error' }),
              getFCRList({ status: 'ignored' }),
              getFCRList({ status: 'completed' }),
            ]).then(([pending, errors, ignored, completed]) => [...errors, ...pending, ...ignored, ...completed]);
          }
          fetchPromise.then(setFcrList).catch(() => setFcrList([]));
        }}
      />
    </div>
  );
}
